'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { PaidAccessError } from '@/exceptions';
import { useGlobal } from '@/contexts/GlobalContext';
import { useToast } from '@/contexts/ToastContext';
import { useModal } from '@/contexts/ModalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import HintModal from '@/components/footballlogs/HintModal';
import LoadingModal from '@/components/common/LoadingModal';
// import TeamNoteSwitchModal from '@/components/footballlogs/TeamNoteSwitchModal';
import FootballlogDatePicker from '@/components/common/datepicker/FootballlogDatePicker';
import FootballlogForm from '@/components/footballlogs/FootballlogForm';
import { FootballlogCreateDataModel } from '@/types/footballlogs';
import CustomNoteDropdown, { CustomNoteCheckState } from '@/components/footballlogs/CustomNoteDropdown';
import TeamNoteSwitcher from '@/components/footballlogs/TeamNoteSwitcher';
import { mutateWithoutLoading } from '@/hooks/swr/useSWRWithLoadingControl';
import { createGbnlog } from '@/repositories/footballlogs';
import { TeamModel, Status } from '@/types/teams';
import { formatYYYYmmddWithHyphen } from '@/utils/date';
// import { getFilePath } from '@/utils';

interface FootballlogDrawerProps {
  propsDate?: Date;
}

/**
 * ドロワー（GBNサッカーノート新規作成）
 * @param param0
 * @returns
 */
const FootballlogDrawer: React.FC<FootballlogDrawerProps> = ({ propsDate }) => {
  /**
   * React context
   */
  const context = useGlobal();
  const { showSuccessMessage, showWarningMessage, showErrorMessage } = useToast();
  const { openModal, closeModal } = useModal();
  const { closeDrawer } = useDrawer();

  const navigate = useNavigate();
  const location = useLocation();

  /**
   * useState
   */
  const [checkStates, setCheckStates] = useState<CustomNoteCheckState>({
    isCheckedScore: false,
    isCheckedMemo: false
  });
  const [dateString, setDateString] = useState<string>(context.systemDate || formatYYYYmmddWithHyphen(new Date()));

  // チームノートのチームID
  const [teamNoteTeamUUID, setTeamNoteTeamUUID] = useState<string>('');

  // 所属チーム
  const [belongTeams, setBelongTeams] = useState<TeamModel[]>([]);

  /**
   * ノートカスタマイズのチェック状態更新
   */
  const handleCheckState = (newStates: CustomNoteCheckState) => {
    setCheckStates(newStates);
  };

  /**
   * チームノート切り替え時のチームIDを設定
   */
  const handleSetTeamNoteTeamUUID = (teamUUID: string) => {
    setTeamNoteTeamUUID(teamUUID);
  };

  /**
   * 日付設定
   */
  const handleSetDateString = (date: Date | null) => {
    const dateStr = date ? formatYYYYmmddWithHyphen(date) : formatYYYYmmddWithHyphen(new Date());
    setDateString(dateStr);
  };

  /**
   * サッカーノート作成
   */
  const handleFormSubmit = async (formData: FootballlogCreateDataModel) => {
    // ローディングモーダル表示
    openModal(<LoadingModal />);

    // 1秒の遅延を追加(更新中であることを見せる)
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      // Gbnlog新規作成
      const res = await createGbnlog(formData, context.csrftoken || '');

      if (!res.ok) {
        // NOTE: 課金機能アクセスエラーの場合
        if (res.code === PaidAccessError.CODE) {
          throw new PaidAccessError('課金機能にアクセスできません');
        } else {
          throw new Error(res.detail);
        }
      }

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後、現在のルーティングを再更新（サーバーサイドで再フェッチ）する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // トースト表示
      showSuccessMessage('サッカーノートを保存しました。');

      // ドロワー閉じる
      closeDrawer();
    } catch (err) {
      if (err instanceof PaidAccessError) {
        showWarningMessage('更新できません。');
      } else {
        // エラートースト表示
        showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      }
      throw err;
    } finally {
      // ローディングモーダル非表示
      closeModal();
    }
  };

  useEffect(() => {
    const dateStr = propsDate
      ? formatYYYYmmddWithHyphen(propsDate)
      : context.systemDate || formatYYYYmmddWithHyphen(new Date());
    setDateString(dateStr);
  }, []);

  useEffect(() => {
    // 所属チームのみを抽出(OGOBはチームノートが書けないので指定していない)
    const teams = context.user_teams?.filter((ut) => ut.status === Status.Approved).map((item) => item.team);
    setBelongTeams(teams || []);
  }, []);

  useEffect(() => {
    // チームノート時は現在表示中チームのIDをセット
    // マイノート時は空白をセット（NOTE: マイページ時はcontext.teamがundefined）
    if (context.isTeamPage) {
      const teamUUID = context.team?.uuid !== undefined ? context.team.uuid : null;
      setTeamNoteTeamUUID(teamUUID || '');
      return;
    }
  }, [belongTeams]);

  return (
    <>
      <div className="h-full flex flex-col w-full bg-white border-0 rounded-t-lg outline-none focus:outline-none overflow-y-auto">
        <div
          className="flex justify-between items-center border-b border-green-500 px-4 py-2 bg-white sticky top-0 z-50 rounded-t-lg"
          style={context.team ? { borderBottom: `solid 1px ${context.team.color_a}` } : {}}>
          <div className="flex items-center">
            {/* {context.isTeamPage && context.team?.team_emblem && (
              <img
                src={getFilePath(context.team.team_emblem.file_path)}
                alt="teamEmblem"
                className="w-5 h-5 me-1 object-contain"
              />
            )} */}
            <div className="flex items-center">
              <FootballlogDatePicker propsDateString={dateString} onChangeDate={handleSetDateString} />
            </div>
          </div>
          <div className="flex space-x-2">
            <div
              className="flex items-center text-xs text-gray-500 hover:text-blue-500 cursor-pointer"
              onClick={() => openModal(<HintModal />)}>
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.529 9.988a2.502 2.502 0 1 1 5 .191A2.441 2.441 0 0 1 12 12.582V14m-.01 3.008H12M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            {!context.isTeamPage && belongTeams.length > 0 && (
              <div className="me-2">
                <TeamNoteSwitcher propsTeamNoteTeamUUID={teamNoteTeamUUID} onChange={handleSetTeamNoteTeamUUID} />
              </div>
            )}
            <CustomNoteDropdown checkStates={checkStates} onChangeState={handleCheckState} />
          </div>
        </div>
        <FootballlogForm
          propsDateString={dateString}
          propsTeamNoteTeamUUID={teamNoteTeamUUID}
          checkStates={checkStates}
          onFormSubmit={handleFormSubmit}
        />
      </div>
    </>
  );
};

export default FootballlogDrawer;
